import React, { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { css } from './UI';

// Genera y descarga un Excel de plantilla
function downloadTemplate(titulo, columnas) {
  const headers = columnas.map(c => c.label + (c.required ? ' *' : ''));
  const example = columnas.map(c => c.ejemplo ?? '');
  const ws = XLSX.utils.aoa_to_sheet([headers, example]);
  ws['!cols'] = headers.map(() => ({ wch: 30 }));
  // Nota de encabezado
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, titulo.slice(0, 31));
  XLSX.writeFile(wb, `plantilla_${titulo.toLowerCase().replace(/\s+/g, '_')}.xlsx`);
}

// Descarga los registros YA cargados con el mismo layout de la plantilla, para que
// el archivo sirva de respaldo y se pueda volver a importar sin retocarlo.
// Los valores van como texto a propósito: así Excel no convierte CLABEs ni cuentas
// a notación científica ni se come los ceros a la izquierda.
export function exportarLayout(titulo, columnas, items) {
  const headers = columnas.map(c => c.label + (c.required ? ' *' : ''));
  const filas = items.map(it => columnas.map(c => {
    const v = it[c.key];
    if (v == null) return '';
    return Array.isArray(v) ? v.join(', ') : String(v);
  }));
  const ws = XLSX.utils.aoa_to_sheet([headers, ...filas]);
  ws['!cols'] = headers.map(() => ({ wch: 30 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, titulo.slice(0, 31));
  const hoy = new Date().toLocaleDateString('en-CA');
  XLSX.writeFile(wb, `${titulo.toLowerCase().replace(/\s+/g, '_')}_${hoy}.xlsx`);
}

// Parsea el archivo y retorna rows con los keys del config
function parseSheet(file, columnas, onReady) {
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const wb = XLSX.read(new Uint8Array(e.target.result), { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, { defval: '' });

      const rows = raw.map(row => {
        const mapped = {};
        columnas.forEach(col => {
          const match = Object.keys(row).find(k =>
            k.replace(' *', '').toLowerCase().trim() === col.label.toLowerCase().trim() ||
            k.replace(' *', '').toLowerCase().trim() === col.key.toLowerCase().trim()
          );
          mapped[col.key] = match ? String(row[match]).trim() : '';
        });
        return mapped;
      }).filter(row => columnas.some(c => row[c.key])); // omitir filas vacías

      onReady(rows);
    } catch (err) {
      onReady([], err.message);
    }
  };
  reader.readAsArrayBuffer(file);
}

function validarRows(rows, columnas) {
  const errores = [];
  rows.forEach((row, i) => {
    columnas.filter(c => c.required).forEach(col => {
      if (!row[col.key]) errores.push(`Fila ${i + 2}: "${col.label}" es requerido`);
    });
  });
  return errores;
}

export function BulkImportModal({ open, onClose, config, onDone }) {
  // config = { titulo, columnas:[{key,label,required,ejemplo}], onImport: async(rows, opcion)=>{insertados,errores:[]},
  //            opcion?: { label, help } } // checkbox opcional; su valor booleano se pasa como 2º arg de onImport
  const [rows, setRows] = useState([]);
  const [validErrors, setValidErrors] = useState([]);
  const [parseError, setParseError] = useState('');
  const [filename, setFilename] = useState('');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [opcion, setOpcion] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setRows([]); setValidErrors([]); setParseError('');
    setFilename(''); setResult(null); setOpcion(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleFile = (file) => {
    if (!file) return;
    reset();
    setFilename(file.name);
    parseSheet(file, config.columnas, (parsed, err) => {
      if (err) { setParseError(err); return; }
      setRows(parsed);
      setValidErrors(validarRows(parsed, config.columnas));
    });
  };

  const handleImport = async () => {
    const validRows = rows.filter((_, i) => {
      return !config.columnas.filter(c => c.required).some(col => !rows[i][col.key]);
    });
    setImporting(true);
    try {
      const res = await config.onImport(validRows, opcion);
      setResult(res);
      if (onDone) onDone();
    } catch (e) {
      setResult({ insertados: 0, errores: [e.message] });
    } finally {
      setImporting(false);
    }
  };

  const validRows = rows.filter(row =>
    !config.columnas.filter(c => c.required).some(col => !row[col.key])
  );
  const preview = rows.slice(0, 5);

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }} onClick={handleClose}>
      <div style={{
        background: 'var(--panel)', borderRadius: 16, width: 740, maxWidth: '96vw',
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 24px 60px rgba(0,0,0,0.22)',
      }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--mesh)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>Importar {config.titulo}</div>
            <div style={{ fontSize: 12, color: 'var(--glass)', marginTop: 2 }}>Carga masiva desde Excel o CSV</div>
          </div>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--glass)', padding: '0 4px' }}>×</button>
        </div>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Paso 1: Plantilla */}
          <div style={{ background: 'var(--well)', borderRadius: 10, padding: '14px 16px', border: '1px solid var(--mesh)' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-soft)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Paso 1 — Descarga la plantilla
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => downloadTemplate(config.titulo, config.columnas)}
                style={{ ...css.btn('#0369a1'), fontSize: 12, padding: '7px 16px' }}>
                ⬇️ Descargar Plantilla Excel
              </button>
              <div style={{ fontSize: 12, color: 'var(--glass)' }}>
                Columnas: {config.columnas.map(c => <span key={c.key} style={{ marginRight: 6 }}>
                  <strong>{c.label}</strong>{c.required ? <span style={{ color: 'var(--danger)' }}> *</span> : ''}
                </span>)}
              </div>
            </div>
          </div>

          {/* Paso 2: Subir archivo */}
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-soft)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Paso 2 — Sube el archivo completado
            </div>
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={e => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
              style={{
                border: `2px dashed ${dragging ? '#0369a1' : 'var(--mesh)'}`,
                borderRadius: 10, padding: '20px', textAlign: 'center',
                cursor: 'pointer', background: dragging ? '#f0f9ff' : '#fafbfc',
                transition: 'all 0.15s',
              }}>
              <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files[0])} />
              <div style={{ fontSize: 28, marginBottom: 6 }}>📊</div>
              {filename ? (
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--st-por-pagar)' }}>{filename}</div>
              ) : (
                <div style={{ fontSize: 13, color: 'var(--glass)' }}>Arrastra un archivo o haz clic para seleccionar</div>
              )}
              <div style={{ fontSize: 11, color: 'var(--glass)', marginTop: 3 }}>.xlsx · .xls · .csv</div>
            </div>
            {parseError && (
              <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--danger-bg)', borderRadius: 8, border: '1px solid #fecaca', fontSize: 12, color: 'var(--danger)' }}>
                Error al leer el archivo: {parseError}
              </div>
            )}
          </div>

          {/* Opción extra (ej. reemplazar catálogo) */}
          {config.opcion && rows.length > 0 && !result && (
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px', background: opcion ? 'var(--danger-bg)' : 'var(--well)', border: `1px solid ${opcion ? 'var(--danger-line)' : 'var(--mesh)'}`, borderRadius: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={opcion} onChange={e => setOpcion(e.target.checked)} style={{ marginTop: 2, cursor: 'pointer' }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: opcion ? 'var(--danger)' : 'var(--ink-soft)' }}>{config.opcion.label}</div>
                {config.opcion.help && <div style={{ fontSize: 11.5, color: 'var(--glass)', marginTop: 2 }}>{config.opcion.help}</div>}
              </div>
            </label>
          )}

          {/* Vista previa */}
          {rows.length > 0 && !result && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-soft)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Vista previa {preview.length < rows.length ? `(primeras ${preview.length} de ${rows.length})` : `(${rows.length} fila${rows.length !== 1 ? 's' : ''})`}
                </div>
                <div style={{ display: 'flex', gap: 10, fontSize: 12 }}>
                  <span style={{ color: 'var(--ok)', fontWeight: 600 }}>✓ {validRows.length} válidas</span>
                  {validErrors.length > 0 && <span style={{ color: 'var(--danger)', fontWeight: 600 }}>✗ {validErrors.length} error{validErrors.length !== 1 ? 'es' : ''}</span>}
                </div>
              </div>
              <div style={{ overflowX: 'auto', border: '1px solid var(--mesh)', borderRadius: 8 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--well)' }}>
                      {config.columnas.map(col => (
                        <th key={col.key} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--glass)', textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.05em', whiteSpace: 'nowrap', borderBottom: '1px solid var(--mesh)' }}>
                          {col.label}{col.required && <span style={{ color: 'var(--danger)' }}> *</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, i) => {
                      const hasError = config.columnas.filter(c => c.required).some(col => !row[col.key]);
                      return (
                        <tr key={i} style={{ background: hasError ? 'var(--danger-bg)' : 'white', borderBottom: '1px solid #f1f5f9' }}>
                          {config.columnas.map(col => (
                            <td key={col.key} style={{ padding: '6px 10px', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: !row[col.key] && col.required ? 'var(--danger)' : '#1e293b' }}>
                              {row[col.key] || <span style={{ color: 'var(--glass)', fontSize: 11 }}>vacío</span>}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {validErrors.length > 0 && (
                <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--danger-bg)', borderRadius: 8, border: '1px solid #fecaca', maxHeight: 100, overflowY: 'auto' }}>
                  {validErrors.slice(0, 10).map((e, i) => (
                    <div key={i} style={{ fontSize: 11, color: 'var(--danger)', marginBottom: 2 }}>{e}</div>
                  ))}
                  {validErrors.length > 10 && <div style={{ fontSize: 11, color: 'var(--danger)' }}>...y {validErrors.length - 10} más</div>}
                </div>
              )}
            </div>
          )}

          {/* Resultado */}
          {result && (
            <div style={{
              padding: '14px 16px', borderRadius: 10,
              background: result.insertados > 0 ? 'rgba(21,128,61,0.08)' : 'var(--danger-bg)',
              border: `1px solid ${result.insertados > 0 ? '#bbf7d0' : 'var(--danger-line)'}`,
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: result.insertados > 0 ? 'var(--ok)' : 'var(--danger)', marginBottom: 6 }}>
                {result.insertados > 0 ? `✅ ${result.insertados} registro${result.insertados !== 1 ? 's' : ''} importado${result.insertados !== 1 ? 's' : ''}` : '⚠️ Sin registros importados'}
                {result.eliminados > 0 && <span style={{ fontWeight: 500, color: 'var(--glass)' }}> · {result.eliminados} anterior{result.eliminados !== 1 ? 'es' : ''} reemplazado{result.eliminados !== 1 ? 's' : ''}</span>}
              </div>
              {result.errores?.length > 0 && (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--danger)', marginBottom: 4 }}>{result.errores.length} error{result.errores.length !== 1 ? 'es' : ''}:</div>
                  {result.errores.slice(0, 8).map((e, i) => (
                    <div key={i} style={{ fontSize: 11, color: 'var(--danger)' }}>{typeof e === 'string' ? e : JSON.stringify(e)}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Botones */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 4, borderTop: '1px solid #f1f5f9' }}>
            <button onClick={handleClose} style={css.btn('#94a3b8', true)}>
              {result ? 'Cerrar' : 'Cancelar'}
            </button>
            {!result && validRows.length > 0 && (
              <button onClick={handleImport} disabled={importing} style={css.btn('#9d4edd')}>
                {importing ? 'Importando...' : `⬆️ Importar ${validRows.length} registro${validRows.length !== 1 ? 's' : ''}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
